import React, { useState } from 'react';
import { useApp } from '../context/AppContext';
import { post, del, patch } from '../lib/api';
import TopBar from '../components/layout/TopBar';
import SectionCard from '../components/widgets/SectionCard';
import { Plus, Trash2, Tags, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';

const TYPES = [
  { value: 'RECEITA', label: 'Receitas', color: '#10B981' },
  { value: 'CUSTO', label: 'Custos', color: '#F59E0B' },
  { value: 'DESPESA', label: 'Despesas', color: '#EF4444' },
  { value: 'INVESTIMENTO', label: 'Investimentos', color: '#3B82F6' },
  { value: 'IMPOSTO', label: 'Impostos', color: '#8B5CF6' },
];

export default function Categorias() {
  const { categories, reloadCategories } = useApp();
  const [tab, setTab] = useState('RECEITA');
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ type: 'RECEITA', name: '' });

  const openCreate = () => {
    setEditingId(null);
    setForm({ type: tab, name: '' });
    setOpen(true);
  };

  const openEdit = (c) => {
    setEditingId(c.id);
    setForm({ type: c.type, name: c.name });
    setOpen(true);
  };

  const submit = async () => {
    if (!form.name.trim()) return toast.error('Nome obrigatório');
    try {
      if (editingId) {
        await patch(`/categories/${editingId}`, form);
        toast.success('Categoria atualizada');
      } else {
        await post('/categories', form);
        toast.success('Categoria criada');
      }
      setOpen(false);
      setEditingId(null);
      setForm({ type: tab, name: '' });
      reloadCategories();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao salvar');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Excluir?')) return;
    await del(`/categories/${id}`);
    reloadCategories();
  };

  const list = categories.filter((c) => c.type === tab);

  return (
    <div className="fade-in">
      <TopBar
        title="Categorias Financeiras"
        subtitle="Organize suas receitas, custos e despesas por categoria"
        action={
          <Button onClick={openCreate} className="btn-gold rounded-md gap-1.5 px-4" data-testid="btn-new-category">
            <Plus size={15} /> Nova Categoria
          </Button>
        }
      />
      <div className="px-8 py-7 space-y-5">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-zinc-900/50 border border-zinc-800 p-1">
            {TYPES.map((t) => (
              <TabsTrigger
                key={t.value}
                value={t.value}
                className="data-[state=active]:bg-zinc-800 data-[state=active]:text-zinc-50 text-zinc-400 text-xs px-4"
                data-testid={`cat-tab-${t.value}`}
              >
                <span className="w-1.5 h-1.5 rounded-full mr-2" style={{ background: t.color }} />
                {t.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        <SectionCard padded={false}>
          {list.length === 0 ? (
            <div className="p-12 text-center text-zinc-500 text-sm">Nenhuma categoria deste tipo</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-2 p-4">
              {list.map((c) => (
                <div key={c.id} className="flex items-center justify-between border border-zinc-800 rounded-md px-4 py-3 bg-zinc-900/40 hover:bg-zinc-900/70 transition-colors" data-testid={`cat-${c.id}`}>
                  <div className="flex items-center gap-2 min-w-0">
                    <Tags size={13} className="text-zinc-500" />
                    <span className="text-sm text-zinc-100 truncate">{c.name}</span>
                  </div>
                  <div className="flex items-center gap-0.5">
                    <button
                      onClick={() => openEdit(c)}
                      className="p-1 rounded hover:bg-amber-900/30 text-zinc-500 hover:text-amber-400"
                      data-testid={`btn-edit-cat-${c.id}`}
                      title="Editar"
                    >
                      <Pencil size={12} />
                    </button>
                    <button
                      onClick={() => remove(c.id)}
                      className="p-1 rounded hover:bg-red-900/30 text-zinc-500 hover:text-red-400"
                      data-testid={`btn-delete-cat-${c.id}`}
                      title="Excluir"
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </SectionCard>
      </div>

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditingId(null); }}>
        <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
          <DialogHeader>
            <DialogTitle className="font-display">
              {editingId ? 'Editar Categoria' : 'Nova Categoria'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Tipo</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger className="bg-zinc-900 border-zinc-800 mt-1"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
                  {TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Nome *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-zinc-900 border-zinc-800 mt-1" data-testid="input-cat-name" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setOpen(false); setEditingId(null); }} className="text-zinc-400 hover:bg-zinc-900">Cancelar</Button>
            <Button onClick={submit} className="btn-gold" data-testid="btn-save-cat">
              {editingId ? 'Salvar' : 'Criar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
